import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import classnames from 'classnames';
import { TrackingHandler } from 'client/tracking/handler';
import { CmsEntities } from 'client/data/models/cms';
import { getFullImageUrl } from 'client/utils/image-helpers';
import { ImpressionTracker } from 'site-modules/shared/components/impression-tracker/impression-tracker';
import { WEBSITE_CLICKOUT_CUSTOM } from 'site-modules/shared/constants/ads';
import { AdLabel } from 'site-modules/shared/components/native-ad/native-ad-components/ad-label/ad-label';
import { CompareAd } from 'site-modules/shared/components/native-ad/compare-ad/compare-ad';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';

import './article-offers-ad.scss';

export const PHOTO_SIZE = 175;
const PHOTO_SIZE_1_UP = 300;

const getMetadataValue = (entry, name, defaultName) =>
  entry.metadata(name).value() || entry.metadata(defaultName).value();

const getImpressionTracker = (trim, entry, platform) =>
  getMetadataValue(entry, `impression-tracker-${platform}-${trim}`, `impression-tracker-${trim}`);

const getLinkUrl = (trim, entry, platform) =>
  getMetadataValue(entry, `link-url-${platform}-${trim}`, `link-url-${trim}`);

const getLinkText = (trim, entry) => getMetadataValue(entry, `link-text-${trim}`);

const getVehicleObject = ({ trim, photo, name, year, make, model, numEntryChildren, entry, platform }) => {
  const impressionTracker = getImpressionTracker(trim, entry, platform);
  const linkUrl = getLinkUrl(trim, entry, platform);
  const linkText = getLinkText(trim, entry);
  const photoSize = numEntryChildren === 1 ? PHOTO_SIZE_1_UP : PHOTO_SIZE;

  return {
    name: name || `${year} ${make} ${model}`,
    photoUrl: getFullImageUrl(get(photo, 'href'), photoSize),
    impressionTracker,
    linkUrl,
    linkText,
  };
};

const handleClick = ({ currentTarget: { dataset } }) =>
  TrackingHandler.fireEvent(
    WEBSITE_CLICKOUT_CUSTOM,
    {},
    {
      action_cause: 'link_click',
      value: dataset.trackingValue,
      ...(dataset.trackingPosition ? { position: dataset.trackingPosition } : {}),
    }
  );

const renderVehicles = vehicleArray =>
  vehicleArray.map(({ name, photoUrl, impressionTracker, linkUrl, linkText }, index) => {
    const content = (
      <Fragment>
        <img className="w-100 rounded mb-0_5" src={photoUrl} alt={name} />
        <ContentFragment componentToUse="figcaption" classes="text-gray-darker fw-bold mb-0_5 heading-5">
          {name}
        </ContentFragment>
        {linkText && (
          <figcaption className="text-primary-darker medium">
            {linkText} <span className="icon-open_in_new size-10" aria-hidden />
          </figcaption>
        )}
      </Fragment>
    );
    return (
      <Col xs={6} md={3} key={name} className="px-md-0_75 pb-1_5 pos-r mb-0" tag="figure">
        {linkUrl ? (
          <a
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="d-block"
            onClick={handleClick}
            data-tracking-value={linkText}
            data-tracking-position={index}
          >
            {content}
          </a>
        ) : (
          content
        )}
        {impressionTracker && <ImpressionTracker src={impressionTracker} className="impression" />}
      </Col>
    );
  });

const renderJellybean = vehicleArray => {
  const { name, photoUrl, impressionTracker } = vehicleArray[0];
  const jellybean = <img className="rounded w-100" src={photoUrl} alt={name} style={{ maxWidth: '300px' }} />;
  return (
    <Col md={7} key={name} className="px-md-0_75 d-flex justify-content-center">
      {jellybean}
      {impressionTracker && <ImpressionTracker src={impressionTracker} className="impression" />}
    </Col>
  );
};

const renderOemLogo = (sponsoredBrandPhotoUrl, sponsoredBrand, classes, styles) => (
  <img
    className={classnames('oem-logo', classes)}
    src={sponsoredBrandPhotoUrl}
    alt={sponsoredBrand}
    style={styles}
    height={22}
  />
);

const renderCta = (isShowOemClickOut, oemDisplayUrl, oemClickOutUrl, oemClickOutText, classes) => {
  if (isShowOemClickOut && oemClickOutUrl && oemClickOutText) {
    return (
      <Fragment>
        <a
          href={oemClickOutUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
          data-tracking-value={oemClickOutText}
        >
          <div className={classnames('btn size-16 btn-sm bg-white btn-outline-primary-b', classes)}>
            {oemClickOutText}
            <span className="icon-open_in_new size-12 ms-0_25" aria-hidden />
          </div>
          {oemDisplayUrl && (
            <span
              className="text-primary-darker mt-0_25 text-center d-block"
              style={{
                marginTop: '4px',
                fontSize: '0.5625rem',
                lineHeight: '0.75rem',
                letterSpacing: '0.024rem',
              }}
            >
              {oemDisplayUrl}
            </span>
          )}
        </a>
      </Fragment>
    );
  }
  return null;
};

export function ArticleOffersAd({ entry, className, isMobile }) {
  if (!get(entry, 'vehicles.length')) {
    return null;
  }

  const platform = isMobile ? 'mobile' : 'wired';
  const title = entry.title;
  const sponsoredBrand = getMetadataValue(entry, 'sponsored-brand');
  const sponsoredBrandPhotoUrl = getMetadataValue(entry, 'sponsored-brand-image-path');
  const oemClickOutText = getMetadataValue(entry, `oemClickOutText-${platform}`, 'oemClickOutText');
  const oemClickOutUrl = getMetadataValue(entry, `oemClickOutUrl-${platform}`, 'oemClickOutUrl');
  const oemDisplayUrl = getMetadataValue(entry, 'oemDisplayUrl');
  const numEntryChildren = entry.children().length;

  const vehicleImages = entry.children().map(childEntry => {
    const photo = get(childEntry.links(), '[0]');
    const name = getMetadataValue(childEntry, 'title');
    const trim = getMetadataValue(childEntry, 'trim');
    return getVehicleObject({ trim, photo, name, numEntryChildren, entry, platform });
  });

  const vehicles = entry.vehicles.map(({ trim, make, model, year }) => {
    const photo = entry.links().find(({ rel }) => rel === trim);
    return getVehicleObject({ trim, photo, year, make, model, numEntryChildren, entry, platform });
  });

  const isShowOemClickOut = numEntryChildren
    ? !vehicleImages.find(({ linkUrl }) => linkUrl)
    : !vehicles.find(({ linkUrl }) => linkUrl);

  if (numEntryChildren === 1) {
    return (
      <Fragment>
        <div
          className={classnames(
            'article-offers-ad is-1-up card pt-2_5 pt-md-1_75 pb-2 pb-md-1_5 px-2 ps-md-0 mx-auto',
            className
          )}
        >
          {renderOemLogo(sponsoredBrandPhotoUrl, sponsoredBrand, 'pos-a', { left: '12px', top: '12px' })}
          <AdLabel />
          <Row className="pb-0_75 pt-0_5 pb-md-0">
            {renderJellybean(vehicleImages)}
            <Col md={5} className="d-flex flex-column justify-content-center pe-md-1_25">
              <ContentFragment componentToUse="h3" classes="heading-4 mt-1 mt-mb-0 mb-3_5 mb-md-3">
                {title}
              </ContentFragment>
              {renderCta(isShowOemClickOut, oemDisplayUrl, oemClickOutUrl, oemClickOutText, 'w-100')}
            </Col>
          </Row>
        </div>
        <CompareAd isMobile={isMobile} />
      </Fragment>
    );
  }

  return (
    <Fragment>
      <div className={classnames('article-offers-ad card pt-1_75 pb-0_25 px-1_5 px-md-2 is-4-up', className)}>
        <AdLabel />
        <div className="d-flex justify-content-between flex-wrap">
          <ContentFragment componentToUse="h3" classes="heading-4 mt-1 mt-md-0">
            {title}
          </ContentFragment>
          <figure className="logo-wrapper m-0">{renderOemLogo(sponsoredBrandPhotoUrl, sponsoredBrand)}</figure>
        </div>
        <Row className="mt-1_25 justify-content-center">
          {numEntryChildren > 1 && renderVehicles(vehicleImages)}
          {!numEntryChildren && vehicles.length && renderVehicles(vehicles)}
        </Row>
        <div className="text-center small mb-0_75">
          {renderCta(isShowOemClickOut, oemDisplayUrl, oemClickOutUrl, oemClickOutText)}
        </div>
      </div>
      <CompareAd isMobile={isMobile} />
    </Fragment>
  );
}

ArticleOffersAd.propTypes = {
  entry: CmsEntities.Content.isRequired,
  className: PropTypes.string,
  isMobile: PropTypes.bool,
};

ArticleOffersAd.defaultProps = {
  className: null,
  isMobile: false,
};
